import React, { ComponentProps } from "react";

import {
  faCircleInfo,
  faCircleNotch,
} from "@fortawesome/pro-regular-svg-icons";

import { TestRunVersionProgress } from "src/api/endpoints";
import { Breakdown } from "src/base-components/Breakdown";
import { Pill } from "src/base-components/Pill";
import { Callout } from "src/design-system/Callout";

const calculateTotalPercentage = (progress: TestRunVersionProgress) =>
  Math.floor(
    ((progress.successful +
      progress.output_mismatch +
      progress.failed +
      progress.ignored) /
      progress.total) *
      100,
  );

export const ProgressDropdown: React.FC<{
  versions: { name: string; progress: TestRunVersionProgress }[];
  showOutputMismatch: boolean;
}> = ({ versions, showOutputMismatch }) => {
  const everyVersionHasFinished = versions.every((version) => {
    return (
      version.progress.total ===
      version.progress.successful +
        version.progress.failed +
        version.progress.ignored +
        version.progress.output_mismatch
    );
  });

  const nothingHasBeenExecutedYet = versions.every(
    (version) =>
      version.progress.successful === 0 &&
      version.progress.failed === 0 &&
      version.progress.ignored === 0 &&
      version.progress.output_mismatch === 0,
  );

  const statusToColors = {
    successful: "green",
    output_mismatch: "yellow",
    failed: "red",
    ignored: "gray",
    waiting: "neutral",
  } as const;

  const statuses = [
    {
      key: "successful",
      color: statusToColors.successful,
      label: "Successful",
      pillColor: "green",
    },
    {
      key: "failed",
      color: statusToColors.failed,
      label: "Failed",
      pillColor: "red",
    },
    {
      key: "output_mismatch",
      color: statusToColors.output_mismatch,
      label: "Output Mismatch",
      pillColor: "yellow",
    },
    {
      key: "ignored",
      color: statusToColors.ignored,
      label: "Ignored",
      pillColor: "gray",
    },
    { key: "total", color: null, label: "Total", pillColor: null },
  ] as const;

  const progressToBreakdownBars = (
    progress: TestRunVersionProgress,
  ): ComponentProps<typeof Breakdown>["bars"] => {
    return [
      { color: statusToColors.successful, count: progress.successful },
      { color: statusToColors.failed, count: progress.failed },
      {
        color: statusToColors.output_mismatch,
        count: progress.output_mismatch,
      },
      { color: statusToColors.ignored, count: progress.ignored },
      {
        color: statusToColors.waiting,
        count:
          progress.total -
          progress.successful -
          progress.failed -
          progress.ignored -
          progress.output_mismatch,
      },
    ];
  };

  const isMultiversion = versions.length > 1;

  const testRunProgressText = everyVersionHasFinished
    ? "Collating results..."
    : "Numbers displayed above are estimates. Detailed results will be available once the test run is complete.";

  return (
    <div className="relative w-122 cursor-default">
      <div className="px-5 pt-4 pb-6">
        {isMultiversion ? (
          <div className="border border-gray-100 text-left text-gray-800">
            <div className="flex h-8 bg-gray-50 font-inter-medium-12px">
              <div className="flex min-w-[96px] items-center border-gray-100 border-r px-2">
                Version
              </div>
              <div className="flex min-w-[120px] items-center border-gray-100 border-r px-2">
                Decisions
              </div>
              <div className="flex flex-1 items-center px-2">Test Status</div>
            </div>
            {versions.map(({ name, progress }) => (
              <div
                key={name}
                className="flex h-8 border-gray-100 border-t font-inter-normal-12px"
              >
                <div className="flex w-[96px] items-center border-gray-100 border-r px-2">
                  <p className="min-w-0 truncate">{name}</p>
                </div>
                <div className="flex min-w-[120px] items-center border-gray-100 border-r px-2">
                  {calculateTotalPercentage(progress)}%
                </div>
                <div className="flex flex-1 items-center px-2">
                  <Breakdown bars={progressToBreakdownBars(progress)} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-gray-100 text-left text-gray-800">
            <div className="flex h-8 bg-gray-50 font-inter-medium-12px">
              <div className="flex min-w-[139px] items-center border-gray-100 border-r px-2">
                Status
              </div>
              <div className="flex min-w-[78px] items-center border-gray-100 border-r px-2">
                Decisions
              </div>
              <div className="flex flex-1 items-center px-2"></div>
            </div>
            {statuses
              .filter(
                ({ key }) => showOutputMismatch || key !== "output_mismatch",
              )
              .map(({ color, key, label, pillColor }) => {
                const progress = versions[0].progress;
                return (
                  <div
                    key={key}
                    className="flex h-8 border-gray-100 border-t font-inter-normal-12px"
                  >
                    <div className="flex w-[139px] items-center border-gray-100 border-r px-2">
                      {pillColor ? (
                        <Pill variant={pillColor}>
                          <Pill.Text>{label}</Pill.Text>
                        </Pill>
                      ) : (
                        label
                      )}
                    </div>
                    <div className="flex min-w-[78px] items-center border-gray-100 border-r px-2">
                      {key === "total"
                        ? calculateTotalPercentage(progress)
                        : Math.floor((progress[key] / progress.total) * 100)}
                      %
                    </div>
                    <div className="flex flex-1 items-center px-2">
                      <Breakdown
                        bars={progressToBreakdownBars(progress)}
                        highlightOnly={color ?? undefined}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
        <div className="mt-3">
          <Callout
            icon={
              nothingHasBeenExecutedYet || everyVersionHasFinished
                ? faCircleNotch
                : faCircleInfo
            }
            isIconSpinning={
              nothingHasBeenExecutedYet || everyVersionHasFinished
            }
            variant="neutral"
          >
            {nothingHasBeenExecutedYet
              ? "Initializing test run"
              : testRunProgressText}
          </Callout>
        </div>
      </div>
    </div>
  );
};
